import React, { useEffect, useState, ChangeEvent, useRef } from 'react';
import progressAnimation from '@/assets/animation/save_loader.json';
import { ReactComponent as UserIcon } from '@/assets/img/user-flat.svg';
import { Button } from '@/components/Elements';
import { FileDetails, useAddPetVaccination, useGetPetVaccination } from '../api/addPetVaccination';
import Lottie from 'lottie-react';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';
import DatePicker, { DayValue, DayRange, Day } from 'react-modern-calendar-datepicker';
import moment from 'moment';
import './PetVaccinationForm.scss';
import { showNotification } from '@/utils/notificationUtils';
import CommonCheckbox from './VaccinationCheckBoxComponent';
import { VaccinationCard, Vaccine } from './VaccinationCard';
import { BackNav } from '@/components/BackNavigation/BackNavigation';
import { Loader } from '@/components/Loader/Loader';
import { useTranslation } from 'react-i18next';

type Props = {
  petId: number;
};

export type VaccinationData = {
  id: string;
  rabies: boolean;
  rabiesExpiry: string;
  rabiesTaken: string;
  rabiesRecord?: FileDetails|null
  dhlpp: boolean;
  dhlppExpiry: string;
  dhlppTaken: string;
  dhlppRecord?: FileDetails|null
  bordetella: boolean;
  bordetellaExpiry: string;
  bordertellaTaken: string;
  bordetellaRecord?: FileDetails|null
  canine: boolean;
  canineExpiry: string;
  canineTaken: string;
  canineRecord?: FileDetails|null
};

export const PetVaccinationForm = ({ petId }: Props) => {
  const { t, i18n } = useTranslation();
  const addPetVaccinationMutation = useAddPetVaccination();
  const getPetVaccinationMutation = useGetPetVaccination();
  const [vaccinationData, setVaccinationData] = useState<VaccinationData>();
  useEffect(() => {
    getVaccinationDetails(petId!);
  }, []);
  const getVaccinationDetails = async (petId: number) => {
    const res = await getPetVaccinationMutation.mutateAsync({
      data: { petId },
    });

    setVaccinationData({
      id: res?.id,
      rabies: res?.rabies,
      rabiesExpiry: res?.rabiesExpiry,
      rabiesTaken: res?.rabiesTaken,
      rabiesRecord: res?.rabiesRecord,
      dhlpp: res?.dhlpp,
      dhlppExpiry: res?.dhlppExpiry,
      dhlppTaken: res?.dhlppTaken,
      dhlppRecord: res?.dhlppRecord,
      canine: res?.canine,
      canineExpiry: res?.canineExpiry,
      canineTaken: res?.canineTaken,
      canineRecord: res?.canineRecord,
      bordetella: res?.bordetella,
      bordetellaExpiry: res?.bordetellaExpiry,
      bordertellaTaken: res?.bordetellaTaken,
      bordetellaRecord: res?.bordetellaRecord,
    });
  };
  const saveVaccinationDetails = async () => {
    if (vaccinationData && validate()) {
      const res = await addPetVaccinationMutation.mutateAsync({
        data: {
          id: vaccinationData.id ? parseInt(vaccinationData.id) : undefined,
          petId: petId,
          rabies: vaccinationData?.rabies,
          rabiesExpiry: vaccinationData?.rabiesExpiry,
          rabiesTaken: vaccinationData?.rabiesTaken,
          rabiesRecord: vaccinationData?.rabiesRecord ? vaccinationData?.rabiesRecord : null,
          dhlpp: vaccinationData?.dhlpp,
          dhlppExpiry: vaccinationData?.dhlppExpiry,
          dhlppTaken: vaccinationData?.dhlppTaken,
          dhlppRecord: vaccinationData?.dhlppRecord ? vaccinationData?.dhlppRecord : null,
          bordetella: vaccinationData?.bordetella,
          bordetellaExpiry: vaccinationData?.bordetellaExpiry,
          bordetellaTaken: vaccinationData?.bordertellaTaken,
          bordetellaRecord: vaccinationData?.bordetellaRecord ? vaccinationData?.bordetellaRecord : null,
          canine: vaccinationData?.canine,
          canineExpiry: vaccinationData?.canineExpiry,
          canineTaken: vaccinationData?.canineTaken,
          canineRecord: vaccinationData?.canineRecord ? vaccinationData?.canineRecord : null,
        },
      });
    }
  };
  const validate = () => {
    if (vaccinationData) {
      const {
        rabies,
        rabiesExpiry,
        dhlpp,
        dhlppExpiry,
        canine,
        canineExpiry,
        bordetella,
        bordetellaExpiry,
      } = vaccinationData;
      if (rabies && (!rabiesExpiry || rabiesExpiry.trim().length < 1)) {
        showNotification({
          type: 'info',
          title: t('Please enter rabies expiriation date'),
        });
        return false;
      } else if (dhlpp && (!dhlppExpiry || dhlppExpiry.trim().length < 1)) {
        showNotification({
          type: 'info',
          title: t('Please enter dhlpp expiriation date'),
        });
        return false;
      } else if (bordetella && (!bordetellaExpiry || bordetellaExpiry.trim().length < 1)) {
        showNotification({
          type: 'info',
          title: t('Please enter bordetella expiriation date'),
        });
        return false;
      } else if (canine && (!canineExpiry || canineExpiry.trim().length < 1)) {
        showNotification({
          type: 'info',
          title: t('Please enter canine expiriation date'),
        });
        return false;
      }
      return true;
    }
  };
  const onFormValueChange = (data: Vaccine) => {
    if (vaccinationData) {
      if (data.type == 'rabies') {
        vaccinationData.rabies = data.taken;
        vaccinationData.rabiesTaken = data.takenDate;
        vaccinationData.rabiesExpiry = data.expiryDate;
        vaccinationData.rabiesRecord = data.fileDetails;
      }
      if (data.type == 'dhlp') {
        vaccinationData.dhlpp = data.taken;
        vaccinationData.dhlppTaken = data.takenDate;
        vaccinationData.dhlppExpiry = data.expiryDate;
        vaccinationData.dhlppRecord = data.fileDetails;
      }
      if (data.type == 'canine') {
        vaccinationData.canine = data.taken;
        vaccinationData.canineExpiry = data.expiryDate;
        vaccinationData.canineTaken = data.takenDate;
        vaccinationData.canineRecord = data.fileDetails;
      }
      if (data.type == 'bordetella') {
        vaccinationData.bordetella = data.taken;
        vaccinationData.bordetellaExpiry = data.expiryDate;
        vaccinationData.bordertellaTaken = data.takenDate;
        vaccinationData.bordetellaRecord = data.fileDetails;
      }
      setVaccinationData({ ...vaccinationData });
    }
  };
  return (
    <div className="vaccination-list tab-content">
      {!getPetVaccinationMutation.isLoading && vaccinationData && (
        <div className="list">
          <VaccinationCard
            title={t('Rabies vaccination')}
            description={t(
              'Vaccine act as an active immunising agent used to prevent infection caused by the rabies virus.'
            )}
            onChange={onFormValueChange}
            petId={petId}
            data={{
              taken: vaccinationData.rabies,
              takenDate: vaccinationData.rabiesTaken,
              expiryDate: vaccinationData.rabiesExpiry,
              type: 'rabies',
              fileDetails: vaccinationData.rabiesRecord,
            }}
          />
          <VaccinationCard
            title={t('DHLPP vaccination')}
            description={t(
              'Vaccine that includes viral diseases, hepatitis, parvovirus, parainfluenza and bacterial infection leptospirosis.'
            )}
            onChange={onFormValueChange}
            petId={petId}
            data={{
              taken: vaccinationData.dhlpp,
              takenDate: vaccinationData.dhlppTaken,
              expiryDate: vaccinationData.dhlppExpiry,
              type: 'dhlp',
              fileDetails: vaccinationData.dhlppRecord,
            }}
          />
          <VaccinationCard
            title={t('Bordetella Vaccination')}
            description={t(
              'A noncore vaccine usually given to dogs that are frequently exposed to other dogs in boarding or social settings.'
            )}
            onChange={onFormValueChange}
            petId={petId}
            data={{
              taken: vaccinationData.bordetella,
              takenDate: vaccinationData.bordertellaTaken,
              expiryDate: vaccinationData.bordetellaExpiry,
              type: 'bordetella',
              fileDetails: vaccinationData.bordetellaRecord,
            }}
          />
          <VaccinationCard
            title={t('Canine Vaccination')}
            description={t(
              "Vaccines help prepare a dog's immune system to defend itself from any invasion of disease-causing organisms."
            )}
            onChange={onFormValueChange}
            petId={petId}
            data={{
              taken: vaccinationData.canine,
              takenDate: vaccinationData.canineTaken,
              expiryDate: vaccinationData.canineExpiry,
              type: 'canine',
              fileDetails: vaccinationData.canineRecord,
            }}
          />
          <div className="action">
            <button className="petsked-button button-dark" onClick={saveVaccinationDetails}>
              {addPetVaccinationMutation.isLoading ? (
                <Loader />
              ) : (
                <span>{t('Update Profile')}</span>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
